import figlet from 'figlet';
import AnsiShadowFont from 'figlet/importable-fonts/ANSI Shadow.js';

// Pre-bundle default FIGlet font (ANSI Shadow) to remove runtime fetch.
// This runs at module load, ensuring font availability synchronously.
figlet.parseFont('ANSI Shadow', AnsiShadowFont);

// Track in local cache so loadFigletFont/ensureFigletFont skip fetch.
const _preloadedFont = 'ANSI Shadow';

// Ensure figlet is globally available for legacy code paths that expect window.figlet
if (typeof window !== 'undefined') {
  window.figlet = figlet;
}

// ---------------------------------------
// FIGlet helpers (no external CDN needed)
// ---------------------------------------

function waitForFiglet() {
  return Promise.resolve(); // figlet is synchronously imported now
}

/**
 * Dynamically load a FIGlet font into the in-memory registry.
 * This avoids bundling dozens of fonts while still allowing
 * runtime ASCII-art generation.
 *
 * @param {string} fontName  Name to register.
 * @param {string} fontUrl   Public URL of the .flf file.
 * @returns {Promise<void>}
 */
export async function loadFigletFont(fontName, fontUrl) {
  await waitForFiglet();
  const res = await fetch(fontUrl);
  if (!res.ok) throw new Error(`Failed to load FIGlet font: ${fontUrl}`);
  const fontData = await res.text();
  figlet.parseFont(fontName, fontData);
}

/**
 * Render ASCII art for `text` using the specified FIGlet font.
 *
 * @param {string} text
 * @param {{font?: string, width?: number}} [opts]
 * @returns {Promise<string>}  Multiline ASCII art.
 */
export async function generateAsciiArt(text, { font = 'ANSI Shadow', width = 80 } = {}) {
    await waitForFiglet();
  return new Promise((resolve, reject) => {
    figlet.text(text, { font, width }, (err, asciiArt) => {
      if (err) reject(err);
      else resolve(asciiArt);
    });
  });
}

/**
 * Synchronous FIGlet render for pre-bundled fonts.
 *
 * @param {string} text
 * @param {{font?: string, width?: number}} [opts]
 * @returns {string} Multiline ASCII art.
 */
export function generateAsciiArtSync(text, { font = 'ANSI Shadow', width = 80 } = {}) {
  return figlet.textSync(text, { font, width });
}

/**
 * Paint pre-generated ASCII art on a 2D canvas using the canonical bitmap font.
 *
 * @param {CanvasRenderingContext2D} ctx        Target context.
 * @param {string} asciiArt                    Multiline string to render.
 * @param {number} x                           Top-left X in pixels.
 * @param {number} y                           Top-left Y in pixels.
 * @param {number} [lineHeight=32]             Distance between lines.
 * @param {string} [color='#39FF14']           Text colour.
 * @param {import('../engine/renderer/bitmapFont').default} bitmapFont Renderer instance.
 */
export function renderAsciiArtWithBitmapFont(ctx, asciiArt, x, y, lineHeight = 32, color = '#39FF14', bitmapFont) {
  const lines = asciiArt.split('\n');
  lines.forEach((line, i) => {
    bitmapFont.drawText(ctx, line, x, y + i * lineHeight, color);
  });
}

// Cache of loaded figlet fonts to avoid multiple fetches
const _loadedFigletFonts = new Set();
_loadedFigletFonts.add(_preloadedFont);

/**
 * Load a FIGlet font only once per session.
 *
 * @param {string} fontName
 * @param {string} fontUrl
 * @returns {Promise<void>}
 */
export async function ensureFigletFont(fontName, fontUrl) {
  if (_loadedFigletFonts.has(fontName)) return;
  await loadFigletFont(fontName, fontUrl);
  _loadedFigletFonts.add(fontName);
}

/**
 * Wrap a text string in an ASCII speech bubble.
 *
 * @param {string} text
 * @param {{maxWidth?: number}} [opts]
 * @returns {string} Bubble-wrapped text (multiline).
 */
export function createSpeechBubble(text, { maxWidth = 38 } = {}) {
  const content = (text ?? '').trim();
  if (!content) return '';

  // Split text into words and build lines respecting maxWidth.
  const words = content.split(/\s+/);
  const lines = [];
  let current = '';
  words.forEach((w) => {
    if ((current + ' ' + w).trim().length > maxWidth) {
      if (current) lines.push(current.trim());
      current = w;
    } else {
      current += ' ' + w;
    }
  });
  if (current.trim()) lines.push(current.trim());

  if (lines.length === 0) return content; // Shouldn’t happen, safety.

  const width = Math.min(maxWidth, Math.max(...lines.map((l) => l.length)));
  const top = '┌' + '─'.repeat(width + 2) + '┐';
  const bottom = '└' + '─'.repeat(width + 2) + '┘';
  const bubbleLines = lines.map((l) => `│ ${l.padEnd(width, ' ')} │`);
  return [top, ...bubbleLines, bottom].join('\n');
} 